import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PurchaseOrder } from '@/schemas/purchaseOrders';

interface PurchaseOrdersState {
  byId: Record<string, PurchaseOrder>;
  allIds: string[];
  filters: {
    collaboratorId?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  };
  selectedId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: PurchaseOrdersState = {
  byId: {},
  allIds: [],
  filters: {},
  selectedId: null,
  isLoading: false,
  error: null,
};

const purchaseOrdersSlice = createSlice({
  name: 'purchaseOrders',
  initialState,
  reducers: {
    setPurchaseOrders: (state, action: PayloadAction<PurchaseOrder[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((po) => {
        state.byId[po.id] = po;
        state.allIds.push(po.id);
      });
    },
    addPurchaseOrder: (state, action: PayloadAction<PurchaseOrder>) => {
      const po = action.payload;
      state.byId[po.id] = po;
      if (!state.allIds.includes(po.id)) {
        state.allIds.push(po.id);
      }
    },
    updatePurchaseOrder: (state, action: PayloadAction<PurchaseOrder>) => {
      const po = action.payload;
      state.byId[po.id] = po;
    },
    deletePurchaseOrder: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setSelectedId: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<PurchaseOrdersState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setPurchaseOrders,
  addPurchaseOrder,
  updatePurchaseOrder,
  deletePurchaseOrder,
  setSelectedId,
  setFilters,
  setLoading,
  setError,
} = purchaseOrdersSlice.actions;

export default purchaseOrdersSlice.reducer;
